import {useEffect,useRef,useState} from 'react';
import type {IntakeItem} from './api';
import {confirmationMatches,confirmStripeTestQuote,loadQuoteOwnedIntake,prepareConfirmation,retainedConfirmation,testQuoteAmount,
  type ConfirmedTestQuote,type ConfirmationRequest,type QuoteAuthority} from './stripeTestQuoteConfirmation';

const hold='The TEST quote is held. Reload owned work or request authorized reconciliation. Do not replace an uncertain request with a new key.';
const amountText=(amount:number)=>(amount/100).toFixed(2);
const rowKey=(row:IntakeItem)=>`${row.organization_id}:${row.order_id}`;

export function MissionControlStripeTestQuote({enabled,identityId,authorityOrganizationId}:{enabled:boolean}&QuoteAuthority) {
  if(enabled!==true)return null;
  return <ScopedQuoteForm key={`${authorityOrganizationId}:${identityId}`} authority={{identityId,authorityOrganizationId}}/>;
}

function ScopedQuoteForm({authority}:{authority:QuoteAuthority}) {
  const [items,setItems]=useState<IntakeItem[]|null>(null);
  const [selected,setSelected]=useState('');
  const [amount,setAmount]=useState('');
  const [review,setReview]=useState<{row:IntakeItem;amountMinor:number}|null>(null);
  const [confirmed,setConfirmed]=useState<ConfirmedTestQuote|null>(null);
  const [message,setMessage]=useState('');
  const [pending,setPending]=useState(true);
  const [reload,setReload]=useState(0);
  const busy=useRef(true),generation=useRef(0);
  const mutation=useRef<AbortController|null>(null);
  const status=useRef<HTMLParagraphElement|null>(null);
  const row=items?.find(item=>rowKey(item)===selected)??null;
  let saved:ConfirmationRequest|null=null;
  let blocked=false;
  if(row){try {saved=retainedConfirmation(authority,row);blocked=saved!==null&&!confirmationMatches(saved,row);}catch{blocked=true;}}

  useEffect(()=>{
    const current=++generation.current;
    setItems(null);setSelected('');setAmount('');setReview(null);setConfirmed(null);setMessage('');setPending(true);busy.current=true;
    loadQuoteOwnedIntake(authority).then(rows=>{if(generation.current===current)setItems(rows);})
      .catch(()=>{if(generation.current===current)setMessage(hold);})
      .finally(()=>{if(generation.current===current){busy.current=false;setPending(false);}});
    // The existing intake helper has no AbortSignal; invalidate its response. Mutations are aborted.
    return ()=>{generation.current++;mutation.current?.abort();};
  },[reload]);

  function choose(value:string) {
    if(busy.current)return;
    setSelected(value);setReview(null);setConfirmed(null);setMessage('');setAmount('');
    const next=items?.find(item=>rowKey(item)===value);if(!next)return;
    try {const retained=retainedConfirmation(authority,next);if(retained)setAmount(amountText(retained.amountMinor));}
    catch {setMessage(hold);}
  }
  function inspect() {
    if(busy.current||!row||blocked)return;
    setConfirmed(null);
    try {
      const amountMinor=testQuoteAmount(amount);
      if(saved&&saved.amountMinor!==amountMinor)throw new Error('Changed request.');
      setReview({row,amountMinor});setMessage('Review the exact TEST quote below. Confirmation does not approve a customer payment.');
    }catch{setReview(null);setMessage(saved?hold:'Enter a USD amount from 0.50 to 999999.99 with at most two decimal places. No rounding is applied.');}
  }
  async function confirm() {
    if(busy.current||!review||blocked)return;
    busy.current=true;setPending(true);setMessage('');
    const current=generation.current,controller=new AbortController();mutation.current=controller;
    try {
      const request=prepareConfirmation(authority,review.row,review.amountMinor);
      const result=await confirmStripeTestQuote(request,review.row,controller.signal);
      if(generation.current!==current||controller.signal.aborted)return;
      setConfirmed(result);setReview(null);setMessage('Exact TEST quote confirmed. The customer must separately approve it; no checkout or payment was created.');
    }catch{
      if(generation.current===current){setItems(null);setSelected('');setReview(null);setConfirmed(null);setMessage(hold);}
    }finally{
      if(generation.current===current){
        busy.current=false;setPending(false);mutation.current=null;
        requestAnimationFrame(()=>{if(generation.current===current)status.current?.focus();});
      }
    }
  }

  return <section className="panel" aria-labelledby="stripe-test-confirmation-title">
    <p className="eyebrow">PROFENZ, INC. · INTERNAL TEST QUOTE</p>
    <h2 id="stripe-test-confirmation-title">Confirm a TEST quote for owned work</h2>
    <p>Choose currently owned work and enter an explicit TEST amount in USD. This does not establish a tariff, approve a customer payment or create a checkout.</p>
    <p ref={status} tabIndex={-1} role="status" aria-live="polite">{message||(pending?'Loading owned work…':'')}</p>
    <button type="button" disabled={pending} onClick={()=>{if(!busy.current)setReload(value=>value+1);}}>Reload owned work for TEST quotes</button>
    {items?.length===0&&<p>No owned ready-for-review work is available.</p>}
    {items&&items.length>0&&<>
      <p>Up to 50 currently owned orders are shown. Visibility is not permission to confirm; the server rechecks custody and quote-management permission.</p>
      <label>Owned order for TEST quote<select disabled={pending} value={selected} onChange={event=>choose(event.target.value)}>
        <option value="">Select owned work</option>{items.map(item=><option key={rowKey(item)} value={rowKey(item)}>{item.service_type_code} · {item.city}, {item.admin_area} · {item.order_id}</option>)}
      </select></label>
      {row&&<>
        <p>Customer organization {row.organization_id} · Order {row.order_id} · Order version {row.order_version} · Details version {row.details_version}</p>
        {blocked?<p>{hold}</p>:<>
          {saved&&<p>A saved confirmation request exists. Its amount and key cannot be replaced. Review it to explicitly repeat the same request.</p>}
          <label>TEST quote amount (USD)<input inputMode="decimal" autoComplete="off" value={amount} readOnly={saved!==null} disabled={pending||confirmed!==null}
            onChange={event=>{setAmount(event.target.value);setReview(null);setConfirmed(null);}}/></label>
          <button type="button" disabled={pending||confirmed!==null} onClick={inspect}>Review this TEST quote</button>
          {review&&<div className="notice">
            <p>Confirm USD {amountText(review.amountMinor)} for customer {review.row.organization_id}, order {review.row.order_id}, order version {review.row.order_version}, details version {review.row.details_version}.</p>
            <button type="button" disabled={pending} onClick={()=>void confirm()}>{saved?'Repeat the same TEST confirmation':'Confirm this exact TEST quote'}</button>
          </div>}
        </>}
      </>}
    </>}
    {confirmed&&<p>Confirmed TEST quote {confirmed.quoteId} · version {confirmed.quoteVersion} · USD {amountText(confirmed.amountMinor)}. Customer approval remains separate.</p>}
  </section>;
}
